import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  createRawStockCode,
  updateRawStockCode
} from '../../redux/actions/rawStockCode/rawStockCodeAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import { fetchColors } from '../../redux/actions/color/colorAction';
import { fetchStones } from '../../redux/actions/stone/stoneAction';
import { fetchStocks } from '../../redux/actions/stock/stockAction';

const initialForm = {
  stock_type_id: '',
  stock_code: '',
  stock_name: '',
  karatage_id: '',
  color_id: '',
  stone_type_id: ''
};

const RawStockCodeForm = ({ selectedRawStockCode, onReset }) => {
  const dispatch = useDispatch();
  const { purities } = useSelector(state => state.purity);
  const { colors } = useSelector(state => state.color);
  const { stones } = useSelector(state => state.stone);
  const { stocks } = useSelector(state => state.stock);
  const { loading, error } = useSelector(state => state.rawStockCode);

  const [formData, setFormData] = useState(initialForm);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchPurities());
    dispatch(fetchColors());
    dispatch(fetchStones());
    dispatch(fetchStocks());
  }, [dispatch]);

  useEffect(() => {
    if (selectedRawStockCode) {
      setFormData({
        stock_type_id: selectedRawStockCode.stock_type_id || '',
        stock_code: selectedRawStockCode.stock_code || '',
        stock_name: selectedRawStockCode.stock_name || '',
        karatage_id: selectedRawStockCode.karatage_id || '',
        color_id: selectedRawStockCode.color_id || '',
        stone_type_id: selectedRawStockCode.stone_type_id || ''
      });
    } else {
      setFormData(initialForm);
    }
  }, [selectedRawStockCode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.stock_type_id) newErrors.stock_type_id = 'Stock Type is required';
    if (!formData.stock_code) newErrors.stock_code = 'Stock Code is required';
    if (!formData.stock_name) newErrors.stock_name = 'Stock Name is required';
    if (!formData.color_id) newErrors.color_id = 'Color is required';
    const selectedStock = stocks.find(s => s.id === formData.stock_type_id);
    if (selectedStock && selectedStock.name === 'Gold') {
      if (!formData.karatage_id) newErrors.karatage_id = 'Karatage is required';
    } else if (selectedStock) {
      if (!formData.stone_type_id) newErrors.stone_type_id = 'Stone Type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      if (selectedRawStockCode) {
        await dispatch(updateRawStockCode(selectedRawStockCode.id, formData));
        setSuccessMessage('Stock code updated successfully!');
      } else {
        await dispatch(createRawStockCode(formData));
        setSuccessMessage('Stock code created successfully!');
      }
      setFormData(initialForm);
      if (onReset) onReset();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      // error handled by redux
    }
  };

  const handleClear = () => {
    setFormData(initialForm);
    if (onReset) onReset();
  };

  return (
    <Paper elevation={3} sx={{ p: 3, boxShadow: 'none', background: 'transparent' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
        {selectedRawStockCode ? 'Edit Stock Code' : 'Add Stock Code'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal" required error={!!errors.stock_type_id}>
          <InputLabel>Stock Type</InputLabel>
          <Select
            name="stock_type_id"
            value={formData.stock_type_id}
            label="Stock Type"
            onChange={handleChange}
          >
            {stocks.map((type) => (
              <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
            ))}
          </Select>
          {errors.stock_type_id && <Typography color="error" variant="caption">{errors.stock_type_id}</Typography>}
        </FormControl>
        <FormControl fullWidth margin="normal" required error={!!errors.stock_code}>
          <TextField
            name="stock_code"
            value={formData.stock_code}
            label="Stock Code"
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.stock_code}
            helperText={errors.stock_code}
          />
        </FormControl>
        <FormControl fullWidth margin="normal" required error={!!errors.stock_name}>
          <TextField
            name="stock_name"
            value={formData.stock_name}
            label="Stock Name"
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.stock_name}
            helperText={errors.stock_name}
          />
        </FormControl>
        {(() => {
          const selectedStock = stocks.find(s => s.id === formData.stock_type_id);
          if (selectedStock && selectedStock.name === 'Gold') {
            return (
              <FormControl fullWidth margin="normal" required error={!!errors.karatage_id}>
                <InputLabel>Karatage</InputLabel>
                <Select
                  name="karatage_id"
                  value={formData.karatage_id}
                  label="Karatage"
                  onChange={handleChange}
                >
                  {purities.map((purity) => (
                    <MenuItem key={purity.id} value={purity.id}>{purity.name}</MenuItem>
                  ))}
                </Select>
                {errors.karatage_id && <Typography color="error" variant="caption">{errors.karatage_id}</Typography>}
              </FormControl>
            );
          } else if (selectedStock) {
            return (
              <FormControl fullWidth margin="normal" required error={!!errors.stone_type_id}>
                <InputLabel>Stone Type</InputLabel>
                <Select
                  name="stone_type_id"
                  value={formData.stone_type_id}
                  label="Stone Type"
                  onChange={handleChange}
                >
                  {stones.map((stone) => (
                    <MenuItem key={stone.id} value={stone.id}>{stone.name}</MenuItem>
                  ))}
                </Select>
                {errors.stone_type_id && <Typography color="error" variant="caption">{errors.stone_type_id}</Typography>}
              </FormControl>
            );
          }
          return null;
        })()}
        <FormControl fullWidth margin="normal" required error={!!errors.color_id}>
          <InputLabel>Color</InputLabel>
          <Select
            name="color_id"
            value={formData.color_id}
            label="Color"
            onChange={handleChange}
          >
            {colors.map((color) => (
              <MenuItem key={color.id} value={color.id}>{color.name}</MenuItem>
            ))}
          </Select>
          {errors.color_id && <Typography color="error" variant="caption">{errors.color_id}</Typography>}
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (selectedRawStockCode ? 'Update' : 'Add')}
          </Button>
          <Button type="button" variant="outlined" color="primary" fullWidth onClick={handleClear}>
            Clear
          </Button>
        </Box>
        {successMessage && <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </form>
    </Paper>
  );
};

export default RawStockCodeForm; 